/**
 * Split content and media - Figma `6377:87197` (and `6377:87354`, `6377:87710`,
 * `6377:88106`).
 *
 * Eyebrow and heading at the top of the card, body and call to action at the bottom,
 * beside a photograph. The layout itself is `shared/MediaSplit` - three other sections
 * in the catalogue draw the same thing.
 *
 * **`cardLayout: 'joined'`** (6373:16052, 2026-10-30) butts photograph and card together, square
 * where they meet and rounded on the pair's outer corners only. `separate`, two tiles on a
 * gutter, is the default and the look before that.
 *
 * **`headingPlacement: 'above'` lifts the heading out of the card**, full width over the
 * row at the size a block title is drawn. Narzedzia online (`3883:71196`) stacks two of
 * these under one such title, with neither card carrying a heading - only a chip, a
 * paragraph and a button. The editor writes the title on the first of the pair and leaves
 * the second's heading empty.
 */
import { Blocks, CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import { MediaSplit } from '../shared/MediaSplit';
import type { SplitContentMediaSection } from '../../types/sections';

export default function SplitContentMedia({ data }: { data: SplitContentMediaSection }) {
  const above = data.headingPlacement === 'above' && Boolean(data.heading);
  const inCard = Boolean(data.heading) && !above;

  return (
    <SectionShell settings={data.settings} name="split-content-media">
      {above ? (
        <h2 className="mb-10 max-w-[968px] text-h3 text-ink">{withHighlight(data.heading)}</h2>
      ) : null}

      <MediaSplit
        media={data.media}
        imageSide={data.imageSide === 'left' ? 'left' : 'right'}
        variant={data.variant === 'plain' ? 'plain' : 'card'}
        joined={data.cardLayout === 'joined'}
        cardTone={data.cardTone === 'white' || data.cardTone === 'blue' ? data.cardTone : 'grey'}
      >
        {/* Without a heading the eyebrow is the whole top row, and the card's
            `justify-between` still pins it against the body below. */}
        {data.eyebrow || inCard ? (
          <div className="flex flex-col gap-6">
            {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
            {inCard ? <h2 className="text-h3 text-ink">{withHighlight(data.heading)}</h2> : null}
          </div>
        ) : null}

        {/* Body-medium, and anchored to the foot of the card with 40px to the button
            (6377:88007). `justify-between` alone only does that when something sits above
            it; a card with neither chip nor heading would carry its text at the top. The
            plain variant keeps its content centred beside the photograph, as drawn. */}
        <div className={`flex flex-col gap-10 ${data.variant === 'plain' ? '' : 'mt-auto'}`}>
          {data.body ? <Blocks value={data.body} className="text-body-md [&>p:last-child]:mb-0" /> : null}
          {data.cta ? <CtaLink value={data.cta} /> : null}
        </div>
      </MediaSplit>
    </SectionShell>
  );
}
